import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { ClipboardList, RefreshCcw, Search, Check, Save } from "lucide-react";
import { getAllUsersRequest } from "../../../redux/actions/userActions";
import { payrollFetchResourceRequest, payrollSaveResourceRequest } from "../../../redux/actions/payrollActions";
import "./PaieCyclePages.css";

const situations = ["NON_RENSEIGNE", "SORTANT", "DECEDE", "MATERNITE", "MALADIE", "ACCIDENT_TRAVAIL", "CONGE_SANS_SALAIRE", "MAINTENU_SANS_SALAIRE", "MALADIE_PROFESSIONNEL"];
const userNameCnss = (user) => user?.fullName || `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.username || `#${user?.id}`;

const SituationCnssMensuellePage = () => {
    const dispatch = useDispatch();
    const payroll = useSelector((state) => state.payroll || {});
    const users = useSelector((state) => state.user?.users || []);
    const [selectedPeriodeId, setSelectedPeriodeId] = useState("");
    const [q, setQ] = useState("");
    const [savingRowId, setSavingRowId] = useState(null);

    useEffect(() => {
        dispatch(getAllUsersRequest());
        dispatch(payrollFetchResourceRequest("periodes"));
        dispatch(payrollFetchResourceRequest("situationsCnss"));
    }, [dispatch]);

    // Auto-select the first available period if none selected
    useEffect(() => {
        if (!selectedPeriodeId && payroll.periodes?.length > 0) {
            setSelectedPeriodeId(String(payroll.periodes[0].id));
        }
    }, [payroll.periodes, selectedPeriodeId]);

    const handleSituationChange = (userId, situationValue) => {
        if (!selectedPeriodeId) {
            Swal.fire({ icon: "warning", text: "Veuillez d'abord sélectionner une période." });
            return;
        }

        // Find if we already have an entry for this user and period
        const existing = (payroll.situationsCnss || []).find(
            (s) => String(s.userId) === String(userId) && String(s.periodeId) === String(selectedPeriodeId)
        );

        setSavingRowId(userId);

        const payload = {
            userId: Number(userId),
            periodeId: Number(selectedPeriodeId),
            situation: situationValue,
            dateDebut: null,
            dateFin: null
        };

        dispatch(payrollSaveResourceRequest("situationsCnss", payload, existing?.id || null));
        
        setTimeout(() => {
            setSavingRowId(null);
        }, 800);
    };

    const activeUsers = useMemo(() => {
        return users.filter(u => u.isActive !== false);
    }, [users]);

    const filteredUsers = useMemo(() => {
        const needle = q.trim().toLowerCase();
        if (!needle) return activeUsers;
        return activeUsers.filter((u) => 
            userNameCnss(u).toLowerCase().includes(needle) || 
            String(u.employeeCode).toLowerCase().includes(needle)
        );
    }, [activeUsers, q]);

    return (
        <div className="pcyc-page">
            <div className="pcyc-header">
                <div>
                    <h2><ClipboardList size={18} />Situation CNSS mensuelle</h2>
                    <p>Déclaration des situations particulières (maladie, maternité, sortant, etc.) par collaborateur pour la période.</p>
                </div>
                <button className="pcyc-btn pcyc-btn-blue" type="button" onClick={() => dispatch(payrollFetchResourceRequest("situationsCnss"))}>
                    <RefreshCcw size={15} /> Actualiser
                </button>
            </div>

            <div className="pcyc-toolbar">
                <label style={{ minWidth: "220px" }}>
                    <span>Période active</span>
                    <select value={selectedPeriodeId} onChange={(e) => setSelectedPeriodeId(e.target.value)}>
                        <option value="">Sélectionner une période</option>
                        {(payroll.periodes || []).map((p) => (
                            <option key={p.id} value={p.id}>{String(p.mois).padStart(2, "0")}/{p.annee} - {p.statut}</option>
                        ))}
                    </select>
                </label>
                <div className="pcyc-search" style={{ marginTop: "14px" }}>
                    <Search size={15} />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher collaborateur..." />
                </div>
                <div className="pcyc-meta">{filteredUsers.length} collaborateur(s)</div>
            </div>

            <div className="pcyc-table-card">
                <table className="pcyc-table">
                    <thead>
                        <tr>
                            <th>Matricule</th>
                            <th>Collaborateur</th>
                            <th>Poste / Entité</th>
                            <th>Situation CNSS</th>
                            <th>Statut d'enregistrement</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.length === 0 ? (
                            <tr><td colSpan="5" className="pcyc-empty">Aucun collaborateur actif</td></tr>
                        ) : (
                            filteredUsers.map((u) => {
                                const currentSituation = (payroll.situationsCnss || []).find(
                                    (s) => String(s.userId) === String(u.id) && String(s.periodeId) === String(selectedPeriodeId)
                                );
                                const activeValue = currentSituation?.situation || "NON_RENSEIGNE";

                                return (
                                    <tr key={u.id}>
                                        <td><span className="pcyc-code">{u.employeeCode || "N/A"}</span></td>
                                        <td><strong>{userNameCnss(u)}</strong></td>
                                        <td>
                                            <div style={{ fontSize: "11px", color: "#6b7280" }}>{u.jobName || "-"}</div>
                                            <div style={{ fontSize: "10px", color: "#9ca3af", fontWeight: 600 }}>{u.entityName || "-"}</div>
                                        </td>
                                        <td>
                                            <select 
                                                value={activeValue} 
                                                onChange={(e) => handleSituationChange(u.id, e.target.value)}
                                                style={{ padding: "6px 10px", width: "240px" }}
                                            >
                                                {situations.map((s) => (
                                                    <option key={s} value={s}>{s}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td>
                                            {savingRowId === u.id ? (
                                                <span style={{ color: "#707ea7", display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "11px", fontWeight: 700 }}>
                                                    <RefreshCcw size={12} className="kms-spin" /> Enregistrement...
                                                </span>
                                            ) : currentSituation ? (
                                                <span style={{ color: "#2e7d32", display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "11px", fontWeight: 700 }}>
                                                    <Check size={14} /> Enregistré en base
                                                </span>
                                            ) : (
                                                <span style={{ color: "#9ca3af", display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "11px", fontWeight: 600 }}>
                                                    Non renseigné (Par défaut)
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default SituationCnssMensuellePage;
